import { useState } from "react";
import { SubBadge } from "@/components/admin/SectionShell";
import { AlertTriangle, Archive, Copy, Download } from "lucide-react";
import { ConfirmDialog } from "./ConfirmDialog";

const ALL_COLS = [
  ["sno", "S.No"], ["name", "Name"], ["wa_name", "Name in Whatsapp"], ["mobile", "Mobile"], ["email", "Email"],
  ["profession", "Profession"], ["place", "Place"], ["program", "Program Name"], ["batch", "Batch#"],
  ["checklist_agreed", "Agreed to Terms"], ["checklist_agreed_at", "Agreed At"], ["created_at", "Registered At"],
];

const label = (r) => `${r.name || "—"} · ${r.mobile || ""}${r.batch ? ` · Batch ${r.batch}` : ""}`;

export const ExportPanel = ({ exportCols, toggleCol, exportSelectedOnly, setExportSelectedOnly, selectedRows, runExport, copyWaNames, archiveSelected, archiving }) => {
  const [dlg, setDlg] = useState(null);
  const n = selectedRows.length;
  const records = selectedRows.map(label);

  const confirmArchive = () => setDlg({ kind: "archive" });
  const proceed = async () => { setDlg(null); await archiveSelected(); };

  return (
  <div className="bg-[#EDF8F3] border border-[#D3EAE0] rounded-3xl p-6 mb-6" data-testid="export-panel">
    <ConfirmDialog
      open={!!dlg}
      testId={dlg?.kind === "archive" ? "archive-confirm" : "empty-confirm"}
      danger={dlg?.kind === "empty"}
      title={dlg?.kind === "archive" ? "Archive & Empty?" : dlg?.stage === 0 ? "Empty selected records?" : `Are you sure ? Do you want to delete? (${dlg?.stage} of 3)`}
      lines={dlg?.kind === "archive"
        ? [`The ${n} selected record(s) below will be copied to Past Batch and removed from this registration list.`]
        : dlg?.stage === 0 ? [`The ${n} selected record(s) below will be permanently deleted from Future Batch.`, "They will NOT be copied to Past Batch."] : ["Are you sure ? Do you want to delete?"]}
      records={dlg?.kind === "archive" || dlg?.stage === 0 ? records : []}
      note={dlg?.kind === "empty" && dlg?.stage > 0 ? `${n} record(s) · confirmation ${dlg.stage} of 3` : undefined}
      confirmLabel={dlg?.kind === "archive" ? "Yes, archive & empty" : dlg?.stage === 3 ? "Yes, delete now" : "Yes"}
      onCancel={() => setDlg(null)}
      onConfirm={proceed}
    />
    <p className="font-serif text-2xl mb-4"><SubBadge /> Export</p>
    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Choose columns</p>
    <div className="flex flex-wrap gap-3 mb-5">
      {ALL_COLS.map(([k, lbl]) => (
        <label key={k} className="inline-flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" data-testid={`col-${k}`} checked={exportCols.includes(k)} onChange={() => toggleCol(k)} className="w-4 h-4 accent-[#1A2E26]" />
          {lbl}
        </label>
      ))}
    </div>
    <div className="flex flex-wrap items-center gap-4">
      <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" data-testid="export-selected-only" checked={exportSelectedOnly} onChange={(e) => setExportSelectedOnly(e.target.checked)} className="w-4 h-4 accent-[#1A2E26]" />
        Only selected rows{n > 0 ? ` (${n})` : ""}
      </label>
      <button data-testid="export-csv-btn" onClick={() => runExport("csv")} className="inline-flex items-center gap-2 rounded-full border border-primary text-primary px-5 py-2.5 text-sm hover:bg-primary hover:text-primary-foreground transition-colors">
        <Download size={16} /> Export CSV
      </button>
      <button data-testid="export-xlsx-btn" onClick={() => runExport("xlsx")} className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:-translate-y-0.5 transition-transform">
        <Download size={16} /> Export Excel
      </button>
      <button data-testid="copy-wa-names-btn" onClick={copyWaNames} className="inline-flex items-center gap-2 rounded-full border border-[#25D366] text-[#128C4B] px-5 py-2.5 text-sm hover:bg-[#25D366] hover:text-white transition-colors">
        <Copy size={16} /> Copy WhatsApp Names
      </button>
    </div>
    <div className="mt-5 pt-5 border-t border-[#D3EAE0] flex flex-wrap items-center gap-4" data-testid="export-selected-actions">
      <p className="text-xs text-muted-foreground w-full sm:w-auto">Selected records in Future Batch: <span className="font-medium text-foreground" data-testid="export-selected-count">{n}</span></p>
      <button data-testid="archive-empty-btn" onClick={confirmArchive} disabled={archiving || n === 0} title="Copy the selected records to Past Batch and remove them from the registration list" className="inline-flex items-center gap-2 rounded-full border border-accent text-accent px-5 py-2.5 text-sm hover:bg-accent hover:text-white transition-colors disabled:opacity-50">
        <Archive size={16} /> {archiving ? "Archiving…" : "Archive & Empty"}
      </button>
      {n === 0 && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><AlertTriangle size={12} /> Tick records in the registrations table first.</span>}
    </div>
  </div>
  );
};
